import { createApp, defineComponent, h, onBeforeUnmount, onMounted, shallowRef } from 'vue'
import { createI18n } from '@drama/i18n'
import { createPinia } from 'pinia'
import '@drama/ui/theme.css'
import './styles/base.css'
import './styles/trpg-shared.css'

import LandingPage from './pages/LandingPage.vue'
import { createVisualGameClient } from './api/client.js'
import SiteNotificationBell from './components/SiteNotificationBell.vue'
import { createAnalyticsClickDirective } from './directives/analytics-click.js'
import { createVideoAnalyticsDirective } from './directives/video-analytics.js'
import { createVisualGameAnalytics, pageDescriptor, sanitizedPageLocation } from './lib/analytics.js'
import { emailCampaignLanding } from './lib/email-attribution.js'
import { installVisualGameGa4 } from './lib/ga4.js'
import { loadGameFonts } from './lib/game-fonts.js'
import { createFirstPartyBehaviorTracker, createSiteI18nRuntime, initialSiteLocale } from './lib/index.js'

const baseUrl = /** @type {any} */ (import.meta).env?.VITE_API_BASE_URL ?? '/'
const envLocale = /** @type {any} */ (import.meta).env?.VITE_LOCALE
const emailLanding = emailCampaignLanding(globalThis.location)
if (emailLanding !== null) globalThis.history.replaceState({}, '', emailLanding)
installVisualGameGa4(import.meta.env.VITE_GA4_MEASUREMENT_ID)
const i18n = createSiteI18nRuntime(createI18n({ locale: initialSiteLocale(envLocale) }))
const api = createVisualGameClient({
  baseUrl,
  getLocale: () => i18n.getLocale(),
})
const behavior = createFirstPartyBehaviorTracker({ tokenStore: api.tokenStore })

if (globalThis.location.pathname === '/videos') {
  globalThis.history.replaceState({}, '', `/dramas${globalThis.location.search}${globalThis.location.hash}`)
}

const LIGHT = 'voko-cinema-light'
const STUDIO = 'vg-studio'
const DARK = 'dark'

/**
 * 每条路由自带主题和通知铃开关，取代原来按组件对象身份比较的写法：
 * 组件改成动态 import 之后不再有稳定的模块级引用可比。
 *
 * load 是 chunk 边界，静态 import 会把 26 个页面全部打进入口包。首页例外用 component 静态引入：
 * index.html 被 SPA 回退到所有路径，没法只给首页注入 modulepreload，而首页又是主要入口；
 * 让它独立成 chunk 只会在入口包之后多一次往返，移动档实测比多带的几 KB 更贵。
 */
const ROUTES = [
  { test: p => p === '/' || p === '/landing', component: LandingPage, theme: LIGHT },
  { test: p => p === '/stories', load: () => import('./pages/StoriesPage.vue'), theme: LIGHT },
  { test: p => p === '/dramas', load: () => import('./pages/VideosPage.vue'), theme: LIGHT },
  { test: p => p === '/search', load: () => import('./pages/SearchPage.vue'), theme: LIGHT },
  { test: p => p === '/live' || p.startsWith('/live/'), load: () => import('./pages/LivePage.vue'), theme: LIGHT },
  { test: p => p === '/login', load: () => import('./pages/LoginPage.vue'), theme: LIGHT },
  { test: p => p === '/legal/privacy' || p === '/legal/terms' || p === '/contact', load: () => import('./pages/LegalInfoPage.vue'), theme: STUDIO, notification: true },
  { test: p => p === '/index' || p === '/play' || p === '/game', load: () => import('./App.vue'), theme: STUDIO },
  { test: p => p === '/story-tree', load: () => import('./pages/StoryTreePage.vue'), theme: STUDIO },
  { test: p => p.startsWith('/story/'), load: () => import('./pages/StoryDetailPage.vue'), theme: LIGHT },
  { test: p => p === '/account', load: () => import('./pages/AccountCenterPage.vue'), theme: LIGHT },
  { test: p => p === '/account/edit', load: () => import('./pages/AccountEditPage.vue'), theme: LIGHT },
  { test: p => p === '/account/settings', load: () => import('./pages/AccountSettingsPage.vue'), theme: LIGHT },
  { test: p => p === '/my-games', load: () => import('./pages/MyGamesPage.vue'), theme: LIGHT },
  { test: p => p === '/my-videos/watch', load: () => import('./pages/MobileOwnVideoPlayerPage.vue'), theme: LIGHT },
  { test: p => p === '/my-videos' || p === '/my-favorites', load: () => import('./pages/MobileVideoLibraryPage.vue'), theme: LIGHT },
  { test: p => p === '/video-generation', load: () => import('./pages/VideoGenerationManagerPage.vue'), theme: LIGHT },
  { test: p => p === '/credit', load: () => import('./pages/MobileCreditPage.vue'), theme: LIGHT },
  { test: p => p === '/credit/history', load: () => import('./pages/MobileCreditHistoryPage.vue'), theme: LIGHT },
  { test: p => p === '/create' || p === '/create/new', load: () => import('./pages/CreatorProjectsPage.vue'), theme: LIGHT },
  { test: p => p === '/billing', load: () => import('./pages/BillingPage.vue'), theme: LIGHT },
  { test: p => p.startsWith('/players/'), load: () => import('./pages/PlayerPage.vue'), theme: STUDIO, notification: true },
  { test: p => p === '/waitlist', load: () => import('./pages/WaitlistPage.vue'), theme: LIGHT, notification: true },
  { test: p => p === '/invite' || p.includes('/invite/'), load: () => import('./pages/InvitePage.vue'), theme: LIGHT, notification: true },
  { test: (p, view) => view === 'history' || p.endsWith('/run-history.html'), load: () => import('./pages/RunHistoryPage.vue'), theme: DARK, notification: true },
  { test: (p, view) => view === 'notes' || p.endsWith('/trpg-notes.html'), load: () => import('./pages/StandaloneNotesPage.vue'), theme: DARK, notification: true },
  { test: p => p.endsWith('/video-center.html'), load: () => import('./pages/VideoCenterPage.vue'), theme: LIGHT },
]
const FALLBACK_ROUTE = ROUTES[0]

/**
 * @param {{ search?: string, pathname?: string }} [locationLike]
 */
function matchRoute(locationLike) {
  const currentLocation = locationLike || /** @type {{ search?: string, pathname?: string }} */ (
    /** @type {{ location?: unknown }} */ (globalThis).location || {}
  )
  const view = new URLSearchParams(currentLocation.search || '').get('view')
  const path = currentLocation.pathname || ''
  return ROUTES.find(route => route.test(path, view)) || FALLBACK_ROUTE
}

const STALE_CHUNK_RELOAD_KEY = 'visual-game-stale-chunk-reload'

async function resolveRoute(route) {
  applyPageTheme(route)
  if (route.component) return route.component
  try {
    const module = await route.load()
    return module.default
  } catch (error) {
    reloadOnceForStaleChunk(error)
    throw error
  }
}

/**
 * 发布切换版本时，仍开着旧页面的用户会去加载已经不存在的 chunk。服务端保留期内的旧产物
 * 能兜住绝大多数情况，超出保留期就只能靠整页刷新拿新入口。
 * sessionStorage 标记保证只刷一次：刷完仍失败时把错误暴露出来，不进入刷新循环。
 */
function reloadOnceForStaleChunk(error) {
  const store = globalThis.sessionStorage
  if (store?.getItem(STALE_CHUNK_RELOAD_KEY)) return
  store?.setItem(STALE_CHUNK_RELOAD_KEY, '1')
  console.warn('[visual-game] 页面资源已过期，刷新以获取新版本', error)
  globalThis.location.reload()
}

const rootRoute = matchRoute()
const currentPath = globalThis.location.pathname || ''
const currentSearch = globalThis.location.search || ''
const hasPreviewToken = new URLSearchParams(currentSearch).has('previewToken')
let currentPage = pageDescriptor(currentPath)
const analytics = createVisualGameAnalytics({
  getContext: () => ({
    app_name: 'visual_game',
    environment: import.meta.env.MODE,
    app_version: import.meta.env.VITE_APP_VERSION || 'unversioned',
    locale: i18n.getLocale(),
    auth_state: api.tokenStore.getAccessToken() ? 'authenticated' : 'anonymous',
    page_type: currentPage.page_type,
  }),
  send: (name, parameters) => {
    behavior.track(name, parameters)
    const gtag = /** @type {any} */ (globalThis).gtag
    if (typeof gtag !== 'function') return
    try {
      gtag('event', name, parameters)
    } catch (error) {
      console.warn('[visual-game] GA4 event delivery failed', error)
    }
  },
})
const AUTH_REQUIRED_PATHS = ['/index', '/play', '/game', '/story-tree', '/create', '/create/new', '/video-generation']

boot()

/**
 * 游戏路由只判断本地是否存在会话。令牌是否有效由后续业务请求统一确认，
 * access token 过期时由 api-sdk 刷新并重放请求。
 */
async function boot() {
  if (AUTH_REQUIRED_PATHS.includes(currentPath) && !hasPreviewToken && !api.tokenStore.hasSession()) {
    const returnTo = `${currentPath}${currentSearch}`
    const loginPath = globalThis.matchMedia('(max-width: 680px)').matches ? '/login' : '/?auth=login'
    globalThis.location.replace(`${loginPath}${loginPath.includes('?') ? '&' : '?'}returnTo=${encodeURIComponent(returnTo)}`)
    return
  }

  // 页面 chunk 和语言包并行下载；非英语用户要等自己的语言包到位再挂载，否则首屏先闪一帧英文
  const [rootComponent] = await Promise.all([resolveRoute(rootRoute), i18n.ready])

  const SiteRoot = defineComponent({
    name: 'SiteRoot',
    setup() {
      const activeRoot = shallowRef(rootComponent)
      const activeRoute = shallowRef(rootRoute)

      async function syncRoute() {
        const nextRoute = matchRoute()
        // 先 await chunk 再换视图，避免切路由时闪一帧空白
        const nextRoot = await resolveRoute(nextRoute)
        activeRoute.value = nextRoute
        activeRoot.value = nextRoot
        currentPage = pageDescriptor(globalThis.location.pathname)
        analytics.track('page_view', {
          page_type: currentPage.page_type,
          page_location: sanitizedPageLocation(globalThis.location),
          page_title: currentPage.page_title,
        })
        globalThis.scrollTo({ top: 0, behavior: 'instant' })
      }

      onMounted(() => {
        globalThis.addEventListener('visual-game-route-change', syncRoute)
        globalThis.addEventListener('popstate', syncRoute)
      })
      onBeforeUnmount(() => {
        globalThis.removeEventListener('visual-game-route-change', syncRoute)
        globalThis.removeEventListener('popstate', syncRoute)
      })

      return () => [
        h(activeRoot.value),
        ...(activeRoute.value.notification ? [h(SiteNotificationBell)] : []),
      ]
    },
  })
  const app = createApp(SiteRoot)
  app.provide('api', api)
  app.provide('analytics', analytics)
  app.provide('i18nRuntime', i18n)
  app.directive('analytics-click', createAnalyticsClickDirective(analytics))
  app.directive('video-analytics', createVideoAnalyticsDirective(analytics))
  app.use(createPinia())
  app.use(i18n.i18n)
  app.mount('#app')
  globalThis.sessionStorage?.removeItem(STALE_CHUNK_RELOAD_KEY)
  globalThis.addEventListener('pagehide', behavior.flushEngagement)
  globalThis.document.addEventListener('visibilitychange', () => {
    if (globalThis.document.visibilityState === 'hidden') behavior.flushEngagement()
    else behavior.resume()
  })
  analytics.track('page_view', {
    page_type: currentPage.page_type,
    page_location: sanitizedPageLocation(globalThis.location),
    page_title: currentPage.page_title,
  })
}

function applyPageTheme(route) {
  document.documentElement.dataset.theme = route.theme
  if (route.theme === STUDIO) loadGameFonts()
}
